// src/pages/TimeTracking.jsx
import { useEffect, useState } from "react";
import { API_BASE_URL } from "../config";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import logo from "../access/img/MetrIQlogo.png";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function TimeTracking() {
  const [logs, setLogs] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [allLogs, setAllLogs] = useState([]);
  const [filterUser, setFilterUser] = useState(""); // by email
  const [filterProject, setFilterProject] = useState(""); // by project name

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/admin/all-time-logs`, {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        let arr = [];
        if (Array.isArray(data)) {
          arr = data;
        } else if (Array.isArray(data.logs)) {
          arr = data.logs;
        } else {
          console.error("Unexpected response:", data);
          arr = [];
        }
        setAllLogs(arr);
        setLogs(arr);
      })
      .catch((err) => {
        console.error("Failed to fetch time logs:", err);
        setLogs([]);
      });
  }, []);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Build filter options from the data
  const userOptions = Array.from(
    new Map(
      (allLogs || [])
        .filter((l) => l && l.email)
        .map((l) => [l.email, { email: l.email, name: l.name }])
    ).values()
  );

  const projectOptions = Array.from(
    new Set((allLogs || []).filter((l) => l && l.project).map((l) => l.project))
  );

  // Apply filters (client-side) before sorting/exporting
  const filteredLogs = (allLogs || []).filter((l) => {
    const byUser = !filterUser || l.email === filterUser;
    const byProject = !filterProject || l.project === filterProject;
    return byUser && byProject;
  });

  const sortedLogs = [...filteredLogs].sort((a, b) => {
    if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
    if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
    return 0;
  });

  const exportToExcel = () => {
    const worksheetData = filteredLogs.map((log) => ({
      User: `${log.name}\n${log.email}`,
      Project: log.project,
      "Start Time": log.start_time,
      "End Time": log.end_time,
      Pause: `${log.pause_minutes || 0} min`,
      Duration: `${log.duration} min`,
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TimeTracking");
    XLSX.writeFile(workbook, "time_tracking_export.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    const logoBase64 = logo;

    // ➤ Ubaci logo
    const logoWidth = 30;
    const logoHeight = 8;
    doc.addImage(logoBase64, "PNG", 14, 10, logoWidth, logoHeight);

    const startY = 10 + logoHeight + 10;

    // Naslov
    doc.setFontSize(18);
    doc.setTextColor(33, 37, 41);
    doc.text("Time Tracking Report", 14, startY);

    // Podnaslov i datum
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Exported: ${new Date().toLocaleString()}`, 14, 35);

    // Tabela
    autoTable(doc, {
      startY: 50,
      head: [["User", "Project", "Start", "End", "Pause", "Duration"]],
      body: filteredLogs.map((log) => [
        `${log.name}\n${log.email}`,
        log.project,
        log.start_time,
        log.end_time,
        `${log.pause_minutes || 0} min`,
        `${log.duration} min`,
      ]),

      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: "#7f30cb",
        textColor: "#fff",
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 40, bottom: 20 },
    });

    // Footer (stranica i potpis)
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.text(`Page ${i} of ${pageCount}`, 180, 290, null, null, "right");
      doc.text("Generated by Metr.IQ", 14, 290);
    }

    doc.save("time_tracking_export.pdf");
  };

  return (
    <>
      <div className="time-tracking-page">
        <Box className="header" mb={2}>
          <Typography variant="h5" fontWeight={500} gutterBottom>
            Time Tracking
          </Typography>
        </Box>

        <Box
          mb={2}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "@media (max-width: 1220px)": {
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "8px",
              padding: "12px 8px",
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
              marginBottom: "10px",
            },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            width={"100%"}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel style={{ fontSize: 12 }}>User</InputLabel>
              <Select
                style={{ borderRadius: 30, fontSize: 12 }}
                label="User"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
              >
                <MenuItem value="">
                  <em style={{ fontSize: 12 }}>All</em>
                </MenuItem>
                {userOptions.map((u) => (
                  <MenuItem
                    style={{ fontSize: 12 }}
                    key={u.email}
                    value={u.email}
                  >
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="tt-project-label" style={{ fontSize: 12 }}>
                Project
              </InputLabel>
              <Select
                style={{ borderRadius: 30, fontSize: 12 }}
                labelId="tt-project-label"
                label="Project"
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
              >
                <MenuItem value="">
                  <em style={{ fontSize: 12 }}>All</em>
                </MenuItem>
                {projectOptions.map((p) => (
                  <MenuItem style={{ fontSize: 12 }} key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              style={{
                fontSize: 10,
                border: "1px solid #999",
                borderRadius: "20px",
              }}
              variant="text"
              onClick={() => {
                setFilterUser("");
                setFilterProject("");
              }}
            >
              Clear filters
            </Button>
          </Stack>

          <Box display="flex" gap={1}>
            <Button
              style={{ borderRadius: 30, fontSize: 12, width: "max-content" }}
              variant="outlined"
              component="span"
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
            >
              Export to Excel
            </Button>
            <Button
              style={{ borderRadius: 30, fontSize: 12, width: "max-content" }}
              variant="outlined"
              component="span"
              startIcon={<PictureAsPdfIcon />}
              onClick={exportToPDF}
            >
              Export to PDF
            </Button>
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === "name" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "project" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "project"}
                    direction={orderBy === "project" ? order : "asc"}
                    onClick={() => handleSort("project")}
                  >
                    Project
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "start_time" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "start_time"}
                    direction={orderBy === "start_time" ? order : "asc"}
                    onClick={() => handleSort("start_time")}
                  >
                    Start Time
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "end_time" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "end_time"}
                    direction={orderBy === "end_time" ? order : "asc"}
                    onClick={() => handleSort("end_time")}
                  >
                    End Time
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "pause_minutes" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "pause_minutes"}
                    direction={orderBy === "pause_minutes" ? order : "asc"}
                    onClick={() => handleSort("pause_minutes")}
                  >
                    Pause
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "duration" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "duration"}
                    direction={orderBy === "duration" ? order : "asc"}
                    onClick={() => handleSort("duration")}
                  >
                    Duration
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No time entries available.
                  </TableCell>
                </TableRow>
              ) : (
                sortedLogs.map((log, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      {log.name}
                      <Typography
                        variant="h6"
                        style={{ fontSize: 12, color: "grey" }}
                      >
                        {log.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{log.project}</TableCell>
                    <TableCell>{log.start_time || "N/A"}</TableCell>
                    <TableCell>{log.end_time || "N/A"}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      - {log.pause_minutes} min
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color:
                          log.duration < 0
                            ? "#d32f2f"
                            : log.duration > 0
                            ? "#388e3c"
                            : "#555",
                      }}
                    >
                      {log.duration} min
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </div>
    </>
  );
}

export default TimeTracking;
